/**
 * A small ring buffer of things that went wrong recently, attached to a
 * feedback report.
 *
 * In practice this is more diagnostic than the screenshot: a user writes
 * "the chart was blank", and what actually identifies the fault is the
 * TypeError thrown three seconds earlier or the 500 from the run endpoint.
 * Neither is something a user can be asked to copy out by hand.
 *
 * Deliberately tiny and dependency-free: it is imported at app startup and
 * has to be safe to run before anything else exists. Entries are capped in
 * both count and length so the captured blob stays small — the server
 * rejects an oversized `context` rather than storing it.
 */

const MAX_CONSOLE_ENTRIES = 20;
const MAX_API_FAILURES = 5;
const MAX_MESSAGE_CHARS = 500;

export interface DiagnosticEntry {
  ts: string;
  kind: 'console.error' | 'window.error' | 'unhandledrejection';
  message: string;
}

export interface ApiFailureEntry {
  ts: string;
  method: string;
  url: string;
  status: number | string;
  detail?: string;
}

const consoleEntries: DiagnosticEntry[] = [];
const apiFailures: ApiFailureEntry[] = [];
let installed = false;

function truncate(value: string): string {
  return value.length > MAX_MESSAGE_CHARS ? `${value.slice(0, MAX_MESSAGE_CHARS)}…` : value;
}

/** Best-effort stringify — console args are arbitrary and may not serialise. */
function describe(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function push<T>(buffer: T[], entry: T, limit: number): void {
  buffer.push(entry);
  if (buffer.length > limit) buffer.splice(0, buffer.length - limit);
}

function record(kind: DiagnosticEntry['kind'], message: string): void {
  push(consoleEntries, { ts: new Date().toISOString(), kind, message: truncate(message) }, MAX_CONSOLE_ENTRIES);
}

/**
 * Record a failed API call. Called from the axios response interceptor
 * rather than by each caller, so nothing has to remember to opt in.
 */
export function recordApiFailure(entry: Omit<ApiFailureEntry, 'ts'>): void {
  push(
    apiFailures,
    { ...entry, ts: new Date().toISOString(), detail: entry.detail ? truncate(entry.detail) : undefined },
    MAX_API_FAILURES,
  );
}

/**
 * Start capturing. Idempotent — a double call (StrictMode double-mount,
 * a hot reload) must not stack console wrappers on top of each other,
 * which would duplicate every entry and eventually recurse.
 */
export function installDiagnostics(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalError = console.error;
  console.error = (...args: unknown[]): void => {
    try {
      record('console.error', args.map(describe).join(' '));
    } catch {
      // Capture must never break the log call it is wrapping.
    }
    originalError.apply(console, args as []);
  };

  window.addEventListener('error', (event: ErrorEvent) => {
    const where = event.filename ? ` (${event.filename}:${event.lineno})` : '';
    record('window.error', `${event.message}${where}`);
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    record('unhandledrejection', describe(event.reason));
  });
}

/** Snapshot for attaching to a report. Copies, so later errors don't mutate it. */
export function readDiagnostics(): { console_errors: DiagnosticEntry[]; api_failures: ApiFailureEntry[] } {
  return { console_errors: [...consoleEntries], api_failures: [...apiFailures] };
}

/** Test seam — the buffers are module state and outlive a single test. */
export function clearDiagnostics(): void {
  consoleEntries.length = 0;
  apiFailures.length = 0;
}
